import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Form, Icon, StatefulButton } from '@openedx/paragon';
import { SpinnerSimple } from '@openedx/paragon/icons';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import activationButtonIcon from './assets/email-icon.svg';
import { ACTIVATE_ACCOUNT, activateAccount, setActivateAccountFormData } from './data/actions';
import { activateAccountResultSelector } from './data/selectors';
import messages from './messages';
import VerificationSuccessPage from './VerificationSuccess';
import BaseContainer from '../base-container';
import { FormGroup } from '../common-components';
import {
  DEFAULT_STATE, EMAIL_ACTIVATION, LOGIN_PAGE,
} from '../data/constants';
import { updatePathWithQueryParams, windowScrollTo } from '../data/utils';
import ForgotPasswordAlert from '../forgot-password/ForgotPasswordAlert';
import validateEmail, { emailRegex } from '../register/RegistrationFields/EmailField/validator';
import validateUsername from '../register/RegistrationFields/UsernameField/validator';

const ActivateAccount = (props) => {
  const {
    status, usernameEmailValidationError, submitState, errorMessage,
  } = props;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [usernameEmail, setUsernameEmail] = useState(props.usernameEmail);
  const [bannerEmail, setBannerEmail] = useState('');
  const [formErrors, setFormErrors] = useState('');
  const [validationError, setValidationError] = useState(usernameEmailValidationError);

  const {
    DISABLE_EMAIL_LOGIN,
    PARENT_EMAIL_AS_PRIMARY,
    DISABLE_EMAIL_OR_USERNAME_LOGIN,
    PLACEHOLDER_USERNAME,
    PLACEHOLDER_EMAIL,
  } = getConfig();

  const ENABLE_ACTIVATION_URL = getConfig().ENABLE_ACTIVATION_URL ?? true;
  const placeholderUsername = PLACEHOLDER_USERNAME || 'username';
  const placeholderEmail = PLACEHOLDER_EMAIL || 'email';

  const isUsernameOrEmailMode = !DISABLE_EMAIL_OR_USERNAME_LOGIN && !PARENT_EMAIL_AS_PRIMARY;
  const isUsernameOnlyMode = (DISABLE_EMAIL_OR_USERNAME_LOGIN && DISABLE_EMAIL_LOGIN) || PARENT_EMAIL_AS_PRIMARY;
  const isEmailOnlyMode = DISABLE_EMAIL_OR_USERNAME_LOGIN && !DISABLE_EMAIL_LOGIN && !PARENT_EMAIL_AS_PRIMARY;
  let fieldLabel = placeholderEmail;
  if (isUsernameOrEmailMode) {
    fieldLabel = `${placeholderUsername} or ${placeholderEmail}`;
  } else if (isUsernameOnlyMode) {
    fieldLabel = placeholderUsername;
  } else if (isEmailOnlyMode) {
    fieldLabel = placeholderEmail;
  }

  useEffect(() => {
    navigate(updatePathWithQueryParams(!ENABLE_ACTIVATION_URL ? LOGIN_PAGE : ''));
  }, [ENABLE_ACTIVATION_URL, navigate]);

  useEffect(() => {
    sendPageEvent('login_and_registration', 'reset');
    sendTrackEvent('edx.bi.password_reset_form.viewed', { category: 'user-engagement' });
  }, []);

  useEffect(() => {
    setValidationError(usernameEmailValidationError);
  }, [usernameEmailValidationError]);

  useEffect(() => {
    if (status === 'complete') {
      setUsernameEmail('');
    }
  }, [status]);

  const getValidationMessage = (value) => {
    if (isUsernameOrEmailMode) {
      if (!value) {
        return formatMessage(messages['activate.account.empty.identity.field.error']);
      }
      if (value.length < 2) {
        return formatMessage(messages['activate.account.identity.less.chars.error']);
      }
      return '';
    }

    if (isUsernameOnlyMode) {
      if (emailRegex.test(value)) {
        return formatMessage(messages['activate.account.username.only.email.not.allowed.error']);
      }
      return validateUsername(value, formatMessage);
    }
    return validateEmail(value, '', formatMessage).fieldError;
  };

  const handleFocus = () => props.setActivateAccountFormData({ usernameEmailValidationError: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    setBannerEmail(usernameEmail);

    const error = getValidationMessage(usernameEmail);
    if (error) {
      setFormErrors(error);
      props.setActivateAccountFormData({ usernameEmail, usernameEmailValidationError: error });
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    } else {
      setFormErrors('');
      props.activateAccount(usernameEmail);
    }
  };

  if (status === ACTIVATE_ACCOUNT.FAILURE) {
    navigate(updatePathWithQueryParams(EMAIL_ACTIVATION));
    return null;
  }

  if (status === 'complete') {
    return (<VerificationSuccessPage fieldLabel={fieldLabel} />);
  }

  let formlabelText = '';
  if (fieldLabel === placeholderUsername) {
    formlabelText = placeholderUsername;
  } else if (fieldLabel === placeholderEmail) {
    formlabelText = placeholderEmail;
  } else {
    formlabelText = `${placeholderUsername} or ${placeholderEmail}`;
  }

  return (
    <BaseContainer>
      <Helmet>
        <title>
          {formatMessage(messages['activate.account.email.page.title'], {
            siteName: getConfig().SITE_NAME,
          })}
        </title>
      </Helmet>
      <div id="main-content" className="main-content activation-page">
        <Form id="activation-form" name="activation-form" className="activation-form">
          <ForgotPasswordAlert
            email={bannerEmail}
            emailError={formErrors}
            status={status}
            errorMessage={errorMessage}
          />
          <h3 className="title-text">{formatMessage(messages['activate.account.email.page.title'])}</h3>
          <div className="title-underline d-flex">
            <div className="underline-bar" />
          </div>
          <div className="content-text mb-2">
            <p className="form-head fs-5">
              {formatMessage(messages['activate.account.page.username.info.text'], { label: fieldLabel })}
            </p>
            <p className="form-text fs-5">
              {getConfig().ACTIVATION_FORM_TEXT || formatMessage(messages['activate.account.form.text'])}
            </p>
            <FormGroup
              floatingLabel={formlabelText}
              name={formatMessage(messages['activate.account.identity.label'], { label: fieldLabel })}
              value={usernameEmail}
              autoComplete="on"
              errorMessage={validationError}
              handleChange={(e) => setUsernameEmail(e.target.value)}
              handleFocus={handleFocus}
            />
            <StatefulButton
              name="let-get-started"
              id="sign-in"
              type="submit"
              variant="brand"
              className="login-button-width"
              state={submitState}
              labels={{
                default: formatMessage(messages['activate.account.page.button.text']),
                pending: '',
              }}
              icons={{
                default: <img src={activationButtonIcon} alt="Activation Button" />,
                pending: <Icon src={SpinnerSimple} className="icon-spin" />,
              }}
              onClick={handleSubmit}
              onMouseDown={(event) => event.preventDefault()}
            />
          </div>
        </Form>
      </div>
    </BaseContainer>
  );
};

ActivateAccount.propTypes = {
  usernameEmail: PropTypes.string,
  usernameEmailValidationError: PropTypes.string,
  activateAccount: PropTypes.func.isRequired,
  setActivateAccountFormData: PropTypes.func.isRequired,
  status: PropTypes.string,
  submitState: PropTypes.string,
  errorMessage: PropTypes.string,
};

ActivateAccount.defaultProps = {
  usernameEmail: '',
  usernameEmailValidationError: '',
  status: null,
  submitState: DEFAULT_STATE,
  errorMessage: '',
};

export default connect(
  activateAccountResultSelector,
  {
    activateAccount,
    setActivateAccountFormData,
  },
)(ActivateAccount);
